import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  FDTitleText, Stack, Table, Box, SecondaryActionIconButton,
} from '@facet-wealth/design';
import NoDataPlaceholderMessage from 'Components/UI/Table/NoDataPlaceholderMessage';
import { currency } from 'Helpers/Formats';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import MetaOverrideSet from 'Views/Page/Content/Templates/NoTemplateTemplate/metaOverrideSet';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import Layout from './Layout';

const styles = (theme) => ({
  tableContainer: {
    paddingTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
});

const Content = ({
  resource,
  classes,
}) => {
  const [expenseResource, setExpenseResource] = useState(resource.attr('additionalClosingCosts').create());

  const { handleChange } = useMetaLayoutContext();

  const handleAddClosingCostExpense = (updatedResource) => {
    // put resource on to collection of additional closing costs
    resource.attr('additionalClosingCosts').resources().pushItem(null, updatedResource);

    // create new empty resource
    setExpenseResource(resource.attr('additionalClosingCosts').create());
  };

  const handleDeleteClosingCostExpense = (attr, index) => {
    resource.attr(attr).resources().removeItemByIndex(index);
    handleChange(attr, resource.attr(attr)); // this sets the value and triggers a re-render so the deleted items disappear
  };

  let totalCostForStandardClosingCosts = 0;
  const standardCostsRows = resource?.attr('standardClosingCosts').resources().list().map((item, i) => {
    totalCostForStandardClosingCosts += parseInt(item.attr('amount'), 10);
    return (
      <Table.BodyRow>
        <Table.BodyCellText value={item.attr('name')} />
        <Table.BodyCellText value={currency(item.attr('amount'), true)} />
        <td>
          <SecondaryActionIconButton
            icon="delete"
            label="Delete"
            onClick={() => handleDeleteClosingCostExpense('standardClosingCosts', i)}
            size="md"
          />
        </td>
      </Table.BodyRow>
    );
  });
  const standardClosingCosts = standardCostsRows.length > 0 ? (
    <React.Fragment>
      <FDTitleText value={`Standard Closing Costs - ${currency(totalCostForStandardClosingCosts, true)}`} />
      {standardCostsRows}
    </React.Fragment>
  ) : null;

  let totalCostForAdditionalClosingCosts = 0;
  const additionalCostsRows = resource?.attr('additionalClosingCosts').resources().list().map((item, i) => {
    totalCostForAdditionalClosingCosts += parseInt(item.attr('amount'), 10);
    return (
      <Table.BodyRow>
        <Table.BodyCellText value={item.attr('name')} />
        <Table.BodyCellText value={currency(item.attr('amount'), true)} />
        <td>
          <SecondaryActionIconButton
            icon="delete"
            label="Delete"
            onClick={() => handleDeleteClosingCostExpense('additionalClosingCosts', i)}
            size="md"
          />
        </td>
      </Table.BodyRow>
    );
  });
  const additionalClosingCosts = additionalCostsRows.length > 0 ? (
    <React.Fragment>
      <FDTitleText value={`Additional Upfront Expenses - ${currency(totalCostForAdditionalClosingCosts, true)}`} />
      {additionalCostsRows}
    </React.Fragment>
  ) : null;

  const noDataMessage = standardCostsRows.length + additionalCostsRows.length === 0 ? (
    <NoDataPlaceholderMessage message="No Upfront Expenses Entered" />
  ) : null;

  return (
    <Stack direction="column" alignItems="flex-start" justifyContent="flex-start">
      <Box width="100%">
        <FDTitleText value="Any additional upfront expenses?" />
        <MetaFormInline
          padding="none"
          resource={expenseResource}
          layout={Layout}
          metaOverrideSet={MetaOverrideSet}
          noIndividualSubmitButtons
          LayoutProps={{
            onAdd: handleAddClosingCostExpense,
          }}
        />
      </Box>
      <div className={classes.tableContainer}>
        <Table.Table>
          <Table.HeadColumnHeaderSet>
            <Table.HeadColumnHeaderCell value="Name" />
            <Table.HeadColumnHeaderCell value="Amount" />
            <Table.HeadColumnHeaderCell value="" />
          </Table.HeadColumnHeaderSet>
          {standardClosingCosts}
          {additionalClosingCosts}
        </Table.Table>
        {noDataMessage}
      </div>
    </Stack>
  );
};

Content.propTypes = {
  classes: PropTypes.object.isRequired,
  resource: PropTypes.object.isRequired,
};

export default withStyles(styles)(Content);
